import React, { useId, useRef } from 'react';
import PinIcon from './CustomIcons/PinIcon';
import Tag, { type TagType } from './Tag';
import Tooltip from './Tooltip';
import TruncatedTooltip from './TruncatedTooltip';
import Text from './Typography/Text';
import { useCardStripSlot } from './cardStripSlot';
import { FOCUS_RING_WITHIN } from './focusRing';
import { useAdoptedStyleSheet } from './useAdoptedStyleSheet';

export interface SelectableCardStatus {
    label: string;
    /** Maps to Tag's palette — `default` (grey) for "Disabled", `warning` (amber) for "Draft". */
    type?: TagType;
}

/** The card's palette: `profile` is green, `library` neutral, `editing` amber for a
 *  profile with unsaved changes. */
export type SelectableCardVariant = 'profile' | 'library' | 'editing';

export interface SelectableCardProps {
    /** Identifies the card inside its group; handed back to `onSelect`. */
    value: string;
    title: string;
    /** Leading glyph on the title line, e.g. a library/book icon. */
    titleIcon?: React.ReactNode;
    /** Pins the card as the account default, marked beside the title. */
    isDefault?: boolean;
    /** Palette only — gradient, borders, indicator and eyebrow; position is settled by the strip. */
    variant?: SelectableCardVariant;
    /** Small label above the title ("Profile", "Explore"). */
    eyebrow?: string;
    /** Status pill rendered next to the eyebrow — or in its place when there is no eyebrow. */
    status?: SelectableCardStatus;
    /** Supporting copy under the title, clamped to two lines. */
    description?: string;
    /** Bottom-left summary ("16 Actions", "10 Selected"). */
    countLabel?: string;
    /** Top-right cluster (markers, an ActionMenu…). The eyebrow row gives way to one
     *  xsmall control; a wider cluster needs a shorter eyebrow and status to clear it. */
    actions?: React.ReactNode;
    selected?: boolean;
    /** Replaces the card's content with a centred in-progress message. */
    loading?: boolean;
    loadingLabel?: string;
    loadingHint?: string;
    /** Blocks selection. Draws no dimming of its own — a switched-off profile is signalled by its `status` tag. */
    disabled?: boolean;
    /** Mutes the title without blocking selection. */
    dimmed?: boolean;
    onSelect?: (value: string) => void;
    /** Tab stop for the card. */
    tabIndex?: number;
    /** Replaces the generated accessible name (eyebrow + title + status). */
    'aria-label'?: string;
    className?: string;
    style?: React.CSSProperties;
}

export const CARD_WIDTH_PX = 240;
const CARD_WIDTH = `${CARD_WIDTH_PX}px`;
const CARD_HEIGHT = '116px';
const CARD_CORNER = 'var(--malachite-radius-xxl, 14px)';
const CARD_RADIUS = `${CARD_CORNER} ${CARD_CORNER} 0 0`;

const MUTED = 'var(--malachite-selectable-card-muted-foreground, #7b7b7b)';
const EYEBROW = 'var(--malachite-text-secondary, #7b7b7b)';

const PROFILE_ACCENT = 'var(--malachite-selectable-card-accent, #047b43)';
const PROFILE_TINT = 'var(--malachite-selectable-card-selected-tint, #e7f8f1)';
const PROFILE_SELECTED_BORDER = 'var(--malachite-selectable-card-selected-border, #047b43)';
const PROFILE_EYEBROW_OPACITY = 0.7;

const LIBRARY_TINT = 'var(--malachite-selectable-card-library-selected-tint, #f1f1f1)';
const LIBRARY_SELECTED_BORDER = 'var(--malachite-selectable-card-library-selected-border, #bbbbbb)';

const EDITING_ACCENT = 'var(--malachite-selectable-card-pending-foreground, #96580a)';
const EDITING_TINT = 'var(--malachite-selectable-card-editing-tint, #f9f2e9)';

const SELECTED_SURFACE_END = 'var(--malachite-selectable-card-selected-surface-end, #ffffff)';

const RESTING_SURFACE = 'var(--malachite-selectable-card-background, #f1f1f1)';
const RESTING_BORDER = '1px solid var(--malachite-selectable-card-border, #dddddd)';

const HOVER_SURFACE_FILL = 'var(--malachite-selectable-card-hover-background, #e8e8e8)';

/** How far a selected card's outline sweeps past its own edge. The strip keeps this much
 *  clear of its scroller's clip. */
export const CARD_FLARE_PX = 14;
const FLARE_PX = CARD_FLARE_PX;
const STROKE_PX = 2;
const CARD_HEIGHT_PX = 116;
const CORNER_PX = 14;

/** Weight of the strip's bottom rule, which a selected card's flare reaches down through. */
export const STRIP_RULE_PX = 2;

/** Where the strip's rule meets the flare. The stroke is centred on its path, so the tip
 *  stops half a stroke inside the flare box. */
export const CARD_FLARE_TIP_PX = FLARE_PX - STROKE_PX / 2;

const FLARE_BOX_WIDTH = CARD_WIDTH_PX + FLARE_PX * 2;
const FLARE_BOX_HEIGHT = CARD_HEIGHT_PX + STRIP_RULE_PX + STROKE_PX;

/** Stroke is centred on the path, so the card's own edges sit half a stroke inside the box. */
const pathTo = (base: number): string => {
    const inset = STROKE_PX / 2;
    const left = FLARE_PX + inset;
    const right = FLARE_PX + CARD_WIDTH_PX - inset;
    const top = inset;
    const r = CORNER_PX - inset;
    return [
        `M${left - FLARE_PX} ${base}`,
        `A${FLARE_PX} ${FLARE_PX} 0 0 0 ${left} ${base - FLARE_PX}`,
        `L${left} ${top + r}`,
        `A${r} ${r} 0 0 1 ${left + r} ${top}`,
        `L${right - r} ${top}`,
        `A${r} ${r} 0 0 1 ${right} ${top + r}`,
        `L${right} ${base - FLARE_PX}`,
        `A${FLARE_PX} ${FLARE_PX} 0 0 0 ${right + FLARE_PX} ${base}`,
    ].join(' ');
};

const OUTLINE_PATH = pathTo(CARD_HEIGHT_PX + STRIP_RULE_PX / 2);

const FILL_PATH = `${OUTLINE_PATH} Z`;

export const FLARE_CLASS = 'malachite-selectable-card-outline';

const ROOT_CLASS = 'malachite-selectable-card';
const STYLE_ID = 'malachite-selectable-card-style';

const OUTLINE_PROPERTY = '--malachite-selectable-card-state-outline';
const TINT_PROPERTY = '--malachite-selectable-card-state-tint';

type CardState = 'resting' | 'hover' | 'selected';
type SettledState = Exclude<CardState, 'hover'>;

type Declarations = Record<string, string>;

const HOVER_SURFACE: Declarations = {
    'background-color': HOVER_SURFACE_FILL,
    'border-left-color': 'transparent',
};

const RESTING_SURFACE_DECLARATIONS: Declarations = {
    'background-color': RESTING_SURFACE,
};

/** Never inline styles: an inline declaration outranks the sheet, so `:hover` could never repaint the card. */
const SURFACES: Record<SelectableCardVariant, Record<CardState, Declarations>> = {
    profile: {
        resting: RESTING_SURFACE_DECLARATIONS,
        hover: HOVER_SURFACE,
        selected: {
            [OUTLINE_PROPERTY]: PROFILE_SELECTED_BORDER,
            [TINT_PROPERTY]: PROFILE_TINT,
        },
    },
    library: {
        resting: RESTING_SURFACE_DECLARATIONS,
        hover: HOVER_SURFACE,
        selected: {
            [OUTLINE_PROPERTY]: LIBRARY_SELECTED_BORDER,
            [TINT_PROPERTY]: LIBRARY_TINT,
        },
    },
    editing: {
        resting: RESTING_SURFACE_DECLARATIONS,
        hover: HOVER_SURFACE,
        selected: {
            [OUTLINE_PROPERTY]: EDITING_ACCENT,
            [TINT_PROPERTY]: EDITING_TINT,
        },
    },
};

const RESTING_EYEBROW: React.CSSProperties = { color: EYEBROW };

const EYEBROWS: Record<SelectableCardVariant, Record<SettledState, React.CSSProperties>> = {
    profile: {
        resting: RESTING_EYEBROW,
        selected: { color: PROFILE_ACCENT, opacity: PROFILE_EYEBROW_OPACITY },
    },
    library: { resting: RESTING_EYEBROW, selected: RESTING_EYEBROW },
    editing: {
        resting: RESTING_EYEBROW,
        selected: { color: EDITING_ACCENT, opacity: PROFILE_EYEBROW_OPACITY },
    },
};

const SCROLLED_SURFACE: Declarations = { 'border-left': RESTING_BORDER };

const OUTLINE_CSS = `
.${ROOT_CLASS}[data-state='selected'] {
    z-index: 5;
}
.${ROOT_CLASS} > .${FLARE_CLASS} {
    position: absolute;
    left: -${FLARE_PX}px;
    top: 0;
    z-index: 0;
    overflow: visible;
    pointer-events: none;
}
.${ROOT_CLASS} > *:not(.${FLARE_CLASS}):not([data-selectable-card-actions]) {
    position: relative;
    z-index: 1;
}
.${ROOT_CLASS} > [data-selectable-card-actions] {
    z-index: 2;
}`;

const bodyOf = (declarations: Declarations): string =>
    Object.entries(declarations)
        .map(([property, value]) => `    ${property}: ${value};`)
        .join('\n');

const selectorFor = (variant: SelectableCardVariant, state: CardState): string =>
    state === 'hover'
        ? `.${ROOT_CLASS}[data-variant='${variant}'][data-state='resting'][data-hoverable]:hover`
        : `.${ROOT_CLASS}[data-variant='${variant}'][data-state='${state}']`;

const ruleFor = (
    variant: SelectableCardVariant,
    state: CardState,
    declarations: Declarations,
): string => `${selectorFor(variant, state)} {\n${bodyOf(declarations)}\n}`;

const SURFACE_CSS = (Object.keys(SURFACES) as SelectableCardVariant[])
    .flatMap((variant) =>
        (Object.keys(SURFACES[variant]) as CardState[]).map((state) =>
            ruleFor(variant, state, SURFACES[variant][state]),
        ),
    )
    .join('\n');

const SLOT_CSS = `.${ROOT_CLASS}[data-pinned='false'][data-state='resting'] {\n${bodyOf(
    SCROLLED_SURFACE,
)}\n}`;

const CARD_CSS = `${SLOT_CSS}\n${SURFACE_CSS}\n${OUTLINE_CSS}`;

const DESCRIPTION_STYLE: React.CSSProperties = {
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    color: MUTED,
    lineHeight: 1.6,
};

const COUNT_STYLE: React.CSSProperties = { color: MUTED, letterSpacing: '0.11px' };

const PENDING = 'var(--malachite-selectable-card-pending-foreground, #96580a)';

const LOADING_LABEL_STYLE: React.CSSProperties = { color: PENDING };
const LOADING_HINT_STYLE: React.CSSProperties = { color: PENDING, opacity: 0.7 };

const PENDING_BODY_TOP_PX = 36;
const PENDING_BODY_BOTTOM_PX = 38;

const TITLE_ICON_STYLE: React.CSSProperties = {
    color: 'var(--malachite-text-primary)',
};

const DEFAULT_MARKER_LABEL = 'Default profile';
const DEFAULT_MARKER_SIZE_PX = 16;
const DEFAULT_MARKER_STYLE: React.CSSProperties = {
    color: PROFILE_ACCENT,
    display: 'inline-flex',
};

/** Announced rather than shown alone: a bare glyph reads as an unnamed graphic, and the tooltip
 *  travels by `aria-describedby`, which describes rather than names. */
const DefaultMarker: React.FC = () => (
    <Tooltip text={DEFAULT_MARKER_LABEL} triggerAs="span" describesTrigger={false}>
        <span role="img" aria-label={DEFAULT_MARKER_LABEL} style={DEFAULT_MARKER_STYLE}>
            <PinIcon size={DEFAULT_MARKER_SIZE_PX} />
        </span>
    </Tooltip>
);

const joinIds = (ids: Array<string | undefined>): string | undefined => {
    const present = ids.filter((id): id is string => id !== undefined);
    return present.length > 0 ? present.join(' ') : undefined;
};

/** A fixed-size card that behaves as one option in a radio group: a card-shaped `Surface.Row`. */
const SelectableCard: React.FC<SelectableCardProps> = ({
    value,
    title,
    titleIcon,
    isDefault = false,
    variant = 'profile',
    eyebrow,
    status,
    description,
    countLabel,
    actions,
    selected = false,
    loading = false,
    loadingLabel = 'Loading…',
    loadingHint,
    disabled = false,
    dimmed = false,
    onSelect,
    tabIndex,
    'aria-label': ariaLabel,
    className = '',
    style,
}) => {
    const { pinned } = useCardStripSlot();
    const hoverable = !selected && !disabled;

    const rootRef = useRef<HTMLDivElement>(null);
    useAdoptedStyleSheet(rootRef, CARD_CSS, STYLE_ID);

    const instanceId = `mal-selectable-card-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;
    const outlineGradientId = `${instanceId}-outline`;
    const eyebrowId = `${instanceId}-eyebrow`;
    const statusId = `${instanceId}-status`;
    const titleId = `${instanceId}-title`;
    const descriptionId = `${instanceId}-description`;
    const countId = `${instanceId}-count`;

    const labelledBy = ariaLabel
        ? undefined
        : joinIds([eyebrow ? eyebrowId : undefined, titleId, status ? statusId : undefined]);
    const describedBy = joinIds([
        description ? descriptionId : undefined,
        countLabel ? countId : undefined,
    ]);

    if (loading) {
        return (
            <div
                ref={rootRef}
                role="status"
                aria-busy="true"
                aria-live="polite"
                data-variant="editing"
                data-state="selected"
                className={`${ROOT_CLASS} relative flex shrink-0 flex-col items-center gap-2 px-5 text-center ${className}`.trim()}
                style={
                    {
                        width: CARD_WIDTH,
                        height: CARD_HEIGHT,
                        borderRadius: CARD_RADIUS,
                        boxSizing: 'border-box',
                        paddingTop: `${PENDING_BODY_TOP_PX}px`,
                        paddingBottom: `${PENDING_BODY_BOTTOM_PX}px`,
                        justifyContent: 'center',
                        [OUTLINE_PROPERTY]: PENDING,
                        [TINT_PROPERTY]: EDITING_TINT,
                        ...style,
                    } as React.CSSProperties
                }
            >
                {/* The same outline a selected card wears: this IS the selection while a profile
                    is being built, so it has to break the strip's rule rather than sit on it. */}
                <svg
                    className={FLARE_CLASS}
                    width={FLARE_BOX_WIDTH}
                    height={FLARE_BOX_HEIGHT}
                    viewBox={`0 0 ${FLARE_BOX_WIDTH} ${FLARE_BOX_HEIGHT}`}
                    fill="none"
                    aria-hidden="true"
                >
                    <defs>
                        <linearGradient id={outlineGradientId} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0" style={{ stopColor: `var(${TINT_PROPERTY})` }} />
                            <stop offset="1" style={{ stopColor: SELECTED_SURFACE_END }} />
                        </linearGradient>
                    </defs>
                    <path
                        d={FILL_PATH}
                        data-malachite-shape=""
                        style={{ fill: `url(#${outlineGradientId})` }}
                    />
                    <path
                        d={OUTLINE_PATH}
                        data-malachite-shape=""
                        strokeWidth={STROKE_PX}
                        style={{ fill: 'none', stroke: `var(${OUTLINE_PROPERTY})` }}
                    />
                </svg>
                <Text size="small" fontWeight="semi-bold" style={LOADING_LABEL_STYLE}>
                    {loadingLabel}
                </Text>
                {loadingHint ? (
                    <Text size="xsmall" fontWeight="semi-bold" style={LOADING_HINT_STYLE}>
                        {loadingHint}
                    </Text>
                ) : null}
            </div>
        );
    }

    const titleInTopRow = variant === 'library';

    const eyebrowStyle = EYEBROWS[variant][selected ? 'selected' : 'resting'];

    return (
        <div
            ref={rootRef}
            data-variant={variant}
            data-pinned={pinned ? 'true' : 'false'}
            data-state={selected ? 'selected' : 'resting'}
            data-hoverable={hoverable ? '' : undefined}
            className={`${ROOT_CLASS} group relative grid shrink-0 grid-rows-[1fr_auto] ${FOCUS_RING_WITHIN} ${className}`.trim()}
            style={{
                width: CARD_WIDTH,
                height: CARD_HEIGHT,
                borderRadius: CARD_RADIUS,
                boxSizing: 'border-box',
                ...style,
            }}
        >
            {selected ? (
                <svg
                    className={FLARE_CLASS}
                    width={FLARE_BOX_WIDTH}
                    height={FLARE_BOX_HEIGHT}
                    viewBox={`0 0 ${FLARE_BOX_WIDTH} ${FLARE_BOX_HEIGHT}`}
                    fill="none"
                    aria-hidden="true"
                >
                    <defs>
                        <linearGradient id={outlineGradientId} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0" style={{ stopColor: `var(${TINT_PROPERTY})` }} />
                            <stop offset="1" style={{ stopColor: SELECTED_SURFACE_END }} />
                        </linearGradient>
                    </defs>
                    <path
                        d={FILL_PATH}
                        data-malachite-shape=""
                        style={{ fill: `url(#${outlineGradientId})` }}
                    />
                    <path
                        d={OUTLINE_PATH}
                        data-malachite-shape=""
                        strokeWidth={STROKE_PX}
                        style={{ fill: 'none', stroke: `var(${OUTLINE_PROPERTY})` }}
                    />
                </svg>
            ) : null}
            <button
                type="button"
                role="radio"
                data-value={value}
                aria-checked={selected}
                aria-label={ariaLabel}
                aria-labelledby={labelledBy}
                aria-describedby={describedBy}
                aria-disabled={disabled || undefined}
                tabIndex={tabIndex ?? (selected ? 0 : -1)}
                onClick={() => {
                    if (disabled || selected) {
                        return;
                    }
                    onSelect?.(value);
                }}
                className={`flex min-w-0 appearance-none flex-col border-0 bg-transparent py-0 pt-3 pr-4 pl-4 text-left outline-none ${
                    disabled ? 'cursor-not-allowed' : 'cursor-pointer'
                }`}
                style={{ borderRadius: CARD_RADIUS }}
            >
                {/* 26px is the xsmall icon button the cluster usually holds, plus a gap: the
                    cluster stands over the end of this row, so the eyebrow stops short of it. */}
                <div
                    className={`flex h-[13px] shrink-0 items-center gap-2 ${
                        actions ? 'pr-[26px]' : 'pr-1'
                    }`}
                >
                    <div className="flex min-w-0 items-center gap-1.5">
                        {titleInTopRow ? (
                            <span id={titleId} className="flex min-w-0 items-center gap-1.5">
                                {titleIcon ? (
                                    <span
                                        className="flex shrink-0 items-center"
                                        style={TITLE_ICON_STYLE}
                                    >
                                        {titleIcon}
                                    </span>
                                ) : null}
                                {isDefault ? (
                                    <span className="flex shrink-0 items-center">
                                        <DefaultMarker />
                                    </span>
                                ) : null}
                                <TruncatedTooltip text={title}>
                                    <Text
                                        size="small"
                                        fontWeight="semi-bold"
                                        style={dimmed ? { color: MUTED } : undefined}
                                    >
                                        {title}
                                    </Text>
                                </TruncatedTooltip>
                            </span>
                        ) : null}
                        {eyebrow ? (
                            <span id={eyebrowId} className="flex min-w-0">
                                <Text size="xsmall" fontWeight="semi-bold" style={eyebrowStyle}>
                                    {eyebrow}
                                </Text>
                            </span>
                        ) : null}
                        {status ? (
                            <span id={statusId} className="flex min-w-0">
                                <Tag
                                    value={status.label}
                                    type={status.type ?? 'default'}
                                    textTransform="none"
                                    paddingSize="compact"
                                />
                            </span>
                        ) : null}
                    </div>
                </div>

                <div
                    className={`mt-[11px] flex min-w-0 flex-1 flex-col gap-1 pt-3 ${
                        titleInTopRow ? 'justify-end' : 'justify-between'
                    }`}
                >
                    {titleInTopRow ? null : (
                        <div id={titleId} className="flex min-w-0 items-center gap-1.5">
                            {titleIcon ? (
                                <span
                                    className="flex shrink-0 items-center"
                                    style={TITLE_ICON_STYLE}
                                >
                                    {titleIcon}
                                </span>
                            ) : null}
                            {isDefault ? (
                                <span className="flex shrink-0 items-center">
                                    <DefaultMarker />
                                </span>
                            ) : null}
                            <TruncatedTooltip text={title}>
                                <Text
                                    size="small"
                                    fontWeight="semi-bold"
                                    style={dimmed ? { color: MUTED } : undefined}
                                >
                                    {title}
                                </Text>
                            </TruncatedTooltip>
                        </div>
                    )}
                    {description ? (
                        <span id={descriptionId} className="block min-w-0">
                            <Text size="xsmall" style={DESCRIPTION_STYLE}>
                                {description}
                            </Text>
                        </span>
                    ) : null}
                </div>
            </button>

            <span id={countId} className="min-w-0 self-end truncate pr-4 pb-3 pl-4">
                {countLabel ? (
                    <Text size="xsmall" fontWeight="semi-bold" style={COUNT_STYLE}>
                        {countLabel}
                    </Text>
                ) : null}
            </span>

            {/* Lifted out of the flow rather than given a grid cell, so it can sit on the
                eyebrow row — which belongs to the control and cannot hold a second one. */}
            {actions ? (
                <div
                    data-selectable-card-actions=""
                    className="absolute top-3 right-4 flex h-[13px] shrink-0 items-center gap-2.5"
                >
                    {actions}
                </div>
            ) : null}
        </div>
    );
};

export default SelectableCard;
export { SelectableCard };
